import {TripbookRepositoryType} from "./interfaces/TripbookRepositoryType.ts";
import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, HttpStatusCode, InternalAxiosRequestConfig} from "axios";
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import {firstValueFrom} from "rxjs";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {Tripbook} from "../domain/entities/Tripbook.ts";
import {AssignInvitationCodeGroupPayload, InvitationCodeRecord, SyncEbookOption, UpdateInvitationCodePayload} from "../domain/entities/InvitationCode.ts";

// Generating the gift PDF in the request adds the PDF render on top of the write,
// so those calls get more room than the default 40s.
const syncEbookConfig = (syncEbook?: boolean): AxiosRequestConfig | undefined =>
    syncEbook ? {params: {ebook: 'sync'}, timeout: 90000} : undefined

export class TripbookRepository implements TripbookRepositoryType {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeout: 40000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        // get token from async storage and set it to the header
        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        // add reponse data converter
        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                // `throw`, not `return`: returning from an onRejected interceptor
                // handler RESOLVES the promise chain — every caller's .catch()
                // (and every React Query mutation's onError/isError) would never
                // fire on a real backend error, silently treating it as success.
                throw generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    getTripbooks(agencyId?: string, keyword?: string): Promise<Tripbook[]> {
        const params: Record<string, string> = {}
        if (agencyId) params.agencyId = agencyId
        if (keyword) params.keyword = keyword
        return this.client.get(`${this.serverBaseURL}/invitation-codes`, {
            params: Object.keys(params).length ? params : undefined,
        })
    }

    createTripbook({syncEbook, ...tripbook}: Tripbook & SyncEbookOption): Promise<Tripbook> {
        if(tripbook.quantity === undefined || tripbook.quantity <= 1) {
            return this.client.put(`${this.serverBaseURL}/invitation-codes`, tripbook, syncEbookConfig(syncEbook))
        } else {
            return this.client.post(`${this.serverBaseURL}/invitation-codes/bulk`, tripbook)
        }

    }

    updateTripbook(tripbook: Tripbook): Promise<Tripbook> {
        return this.client.post(`${this.serverBaseURL}/invitation-codes/`+tripbook.id, tripbook)

    }

    getInvitationCodesByAgency(agencyId: string): Promise<InvitationCodeRecord[]> {
        return this.client.get(`${this.serverBaseURL}/invitation-codes`, {params: {agencyId: agencyId}})
    }

    updateInvitationCode({syncEbook, ...payload}: UpdateInvitationCodePayload & SyncEbookOption): Promise<InvitationCodeRecord> {
        return this.client.post(`${this.serverBaseURL}/invitation-codes/${payload.id}`, payload, syncEbookConfig(syncEbook))
    }

    assignInvitationCodeGroup(payload: AssignInvitationCodeGroupPayload): Promise<InvitationCodeRecord[]> {
        return this.client.post(`${this.serverBaseURL}/invitation-codes/group`, payload)
    }

    deleteTripbook(tripbookId: string): Promise<void> {
        return this.client.delete(`${this.serverBaseURL}/invitation-codes/`+tripbookId)
    }

    warmTripbook(): Promise<void> {
        return this.client.get(`${this.serverBaseURL}/health`)
    }

    shareTripbook(tripbookId: string, email: string): Promise<void> {
        return this.client.post(`${this.serverBaseURL}/invitation-codes/`+tripbookId+'/send-email',{
            destination: email
        })
    }

    downloadEbook(invitationCode: string): Promise<void> {
        return this.client.get(`${this.serverBaseURL}/invitation-codes/`+invitationCode+'/ebook',
            {
                responseType: 'blob'
            })
    }

    // Re-renders the gift PDF server-side. Same render cost as ?ebook=sync, so it
    // gets the same 90s budget. Asked as a blob in case the backend answers with
    // the fresh PDF itself — callers check for a real application/pdf body.
    regenerateEbook(invitationCode: string): Promise<Blob | undefined> {
        return this.client.post(`${this.serverBaseURL}/invitation-codes/${invitationCode}/ebook`, undefined,
            {
                responseType: 'blob',
                timeout: 90000
            })
    }

    cancelInvitationCode(invitationCodeId: string): Promise<InvitationCodeRecord> {
        return this.client.post(`${this.serverBaseURL}/invitation-codes/${invitationCodeId}/cancel`)
    }



}
